import React, { useEffect, useRef, useState } from 'react'
import styled, { css, keyframes } from 'styled-components'
import ChatPanel from './ChatPanel'
import layiMark from './assets/layi-mark.svg'
import { trackChatbotPanelClosed, trackChatbotPanelOpened } from '../../chatbot/chatbotTracking'

// Floating LAYI entry point: the LAYI mark (stacked LAYERS diamonds with a face) top-right (below
// the 60px TopBar), with a speech
// bubble inviting the click, that expands into the chat panel. zIndex must stay STRICTLY below 10:
// the user menu renders inside the TopBar's `position: sticky; z-index: 10` stacking context, so its
// own z-index (15) never competes out here — the menu's effective level is the TopBar's 10, and at
// a tie the widget would still win on document order. 9 keeps the widget above the map toolbars
// (z 1-2) and the LeftBar (8), and below TopBar/menu (10) and the app modals (1200).

const TAIL_SIZE = 8
const TOP_BAR_HEIGHT = 60

// Expanded, the chat is a right aside from the TopBar down to the bottom of the screen. Below twice
// its width it would cover more than half the screen, so it takes the whole width instead and
// covers the page, LeftBar included, until it is collapsed.
const ASIDE_WIDTH = 400
const FULL_WIDTH_BELOW = 2 * ASIDE_WIDTH

const Wrapper = styled.div`
  position: fixed;
  right: 8px;
  top: 72px;
  z-index: 9;
  ${({ docked }) =>
    docked &&
    css`
      top: ${TOP_BAR_HEIGHT}px;
      right: 0;
      bottom: 0;
      width: ${ASIDE_WIDTH}px;

      @media (max-width: ${FULL_WIDTH_BELOW - 1}px) {
        width: 100%;
      }
    `}
`

// Idle: a slow bob with a slight tilt — alive, but not distracting.
const idleBob = keyframes`
  0% { transform: translateY(0) rotate(-2deg); }
  50% { transform: translateY(-6px) rotate(2deg); }
  100% { transform: translateY(0) rotate(-2deg); }
`

const Pill = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0;
  border: none;
  background: none;
  font-family: 'Raleway', sans-serif;
  font-size: 14px;
  cursor: pointer;
`

// The bubble is the same dark navy as the top bar and the LAYI chat background, in both OS themes.
// The tail triangles inherit the look through the custom properties.
const SpeechBubble = styled.span`
  /* Navy card with a mint rim. The rim is the OPAQUE result of mint at 35% over the navy —
     a translucent rim would render one color over the bubble and another over the map behind
     the tail triangles, splitting the outline in two tones. */
  --bubble-bg: #061e2d;
  --bubble-border: #0c665c;
  --bubble-text: #ffffff;

  position: relative;
  padding: 8px 12px;
  border: 1px solid var(--bubble-border);
  border-radius: 14px;
  background: var(--bubble-bg);
  color: var(--bubble-text);
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.18);
  white-space: nowrap;

  /* Tail pointing at the mascot: a border-colored triangle with a bubble-colored one on top of it. */
  &::before,
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 0;
    height: 0;
    border-style: solid;
    transform: translateY(-50%);
  }

  &::before {
    right: -${TAIL_SIZE + 1}px;
    border-width: ${TAIL_SIZE}px 0 ${TAIL_SIZE}px ${TAIL_SIZE + 1}px;
    border-color: transparent transparent transparent var(--bubble-border);
  }

  &::after {
    right: -${TAIL_SIZE - 1}px;
    border-width: ${TAIL_SIZE - 1}px 0 ${TAIL_SIZE - 1}px ${TAIL_SIZE - 1}px;
    border-color: transparent transparent transparent var(--bubble-bg);
  }
`

const PillAvatar = styled.img`
  width: 64px;
  height: 64px;
  object-fit: contain;
  /* The mark's background is transparent: drop-shadow hugs its silhouette where box-shadow would
     draw a rectangular slab behind the whole img box. */
  filter: drop-shadow(0 6px 12px rgba(0, 0, 0, 0.3));
  animation: ${idleBob} 2.8s ease-in-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  @media (prefers-color-scheme: dark) {
    filter: drop-shadow(0 6px 14px rgba(0, 0, 0, 0.55));
  }
`

// The pop-out sizes itself to the screen it opens on — 90% of the available work area, capped at a
// comfortable reading size on large monitors. Browsers clamp oversized requests to the work area
// themselves, so the caps express intent rather than safety — and cover environments whose screen
// reports 0×0 (jsdom). Reopening while the named window is still open reuses it at whatever size
// the user left it; the requested size only applies to a fresh window.
const POPOUT_MAX_WIDTH = 1000
const POPOUT_MAX_HEIGHT = 900
const POPOUT_SCREEN_FRACTION = 0.9

export const getPopoutFeatures = ({ availWidth, availHeight } = window.screen) => {
  const width = Math.min(POPOUT_MAX_WIDTH, Math.round(availWidth * POPOUT_SCREEN_FRACTION) || POPOUT_MAX_WIDTH)
  const height = Math.min(POPOUT_MAX_HEIGHT, Math.round(availHeight * POPOUT_SCREEN_FRACTION) || POPOUT_MAX_HEIGHT)
  return `width=${width},height=${height}`
}

// Collapsed, the panel is hidden rather than unmounted: the embedded LAYI keeps its session and
// the conversation, so reopening shows the chat where it was instead of a fresh one.
const PanelSlot = styled.div`
  display: ${props => (props.visible ? 'block' : 'none')};
  height: 100%;
`

export default function ChatbotWidget({ locale, t }) {
  const [expanded, setExpanded] = useState(false)
  const [panelMounted, setPanelMounted] = useState(false)
  const pillRef = useRef(null)
  const wasExpandedRef = useRef(false)
  const openedAtRef = useRef(null)

  // Collapsing hides the panel and drops focus on <body>; hand it back to the pill so a
  // keyboard user lands where they started.
  useEffect(() => {
    if (wasExpandedRef.current && !expanded && pillRef.current) pillRef.current.focus()
    wasExpandedRef.current = expanded
  }, [expanded])

  const expand = () => {
    openedAtRef.current = Date.now()
    trackChatbotPanelOpened({ page: window.location.pathname })
    setPanelMounted(true)
    setExpanded(true)
  }

  const collapse = () => {
    trackChatbotPanelClosed({ secondsOpen: openedAtRef.current ? Math.round((Date.now() - openedAtRef.current) / 1000) : undefined })
    setExpanded(false)
  }

  // The pop-out is the native Chainlit UI in its own window, same-origin under /layi: it rides the
  // access_token cookie the embedded panel logged in with, so it opens authenticated, on the thread
  // the panel was showing. The panel collapses (as the ✕ does, but without reporting a close).
  const popOut = target => {
    setExpanded(false)
    window.open(target, 'layi-chat', getPopoutFeatures())
  }

  return (
    <Wrapper docked={expanded}>
      {!expanded && (
        /* styled-components v3: innerRef hands over the DOM node (plain ref gives the wrapper) */
        <Pill type="button" onClick={expand} innerRef={node => (pillRef.current = node)}>
          <SpeechBubble>{t('layi_can_i_help_you')}</SpeechBubble>
          <PillAvatar src={layiMark} alt="LAYI" />
        </Pill>
      )}
      {panelMounted && (
        <PanelSlot visible={expanded} aria-hidden={!expanded}>
          <ChatPanel locale={locale} t={t} onClose={collapse} onPopOut={popOut} />
        </PanelSlot>
      )}
    </Wrapper>
  )
}
